import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  Spinner,
  Table,
  TextInput,
  Textarea,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import {
  MdDeleteOutline,
  MdLocalPrintshop,
  MdOutlineAttachMoney,
} from "react-icons/md";
import { BsFillBarChartFill, BsTrash } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import { useReactToPrint } from "react-to-print";
import { FiExternalLink } from "react-icons/fi";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { LuFilter, LuFilterX } from "react-icons/lu";
import CurrencyInput from "../inputs/CurrencyInput";
import { useForm, useWatch } from "react-hook-form";

const DATE_FORMAT = "DD/MM/YYYY";

/**
 * AdminPaymentsTable — versão refatorada, com:
 * - CurrencyInput aceitando vírgula e ponto (parse flexível) e exibindo em pt-BR
 * - Alinhamento de colunas (principal e no modal de selecionados)
 * - Layout de filtros/toolbar simplificado
 * - Estados e efeitos organizados
 */
const AdminPaymentsTable = ({ isActive = true }) => {
  const queryClient = useQueryClient();

  // ===================== LISTAGEM / FILTROS =====================
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);

  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });

  const reportRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: reportRef });

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypePayments, setSelectedTypePayments] = useState([]);

  const [openModalPaymentConfirm, setOpenModalPaymentConfirm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [errorPaymentConfirm, setErrorPaymentConfirm] = useState(false);
  const [errorPaymentDelete, setErrorPaymentDelete] = useState(false);
  const [openModalPaymentDelete, setOpenModalPaymentDelete] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);

  const [errorInvoicesUI, setErrorInvoicesUI] = useState(false);

  useEffect(() => {
    if (showFilters) setRenderFilters(true);
    else {
      const t = setTimeout(() => setRenderFilters(false), 450);
      return () => clearTimeout(t);
    }
  }, [showFilters]);

  // Presets de datas
  const presets = [
    {
      label: "Hoje",
      get: () => [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
    },
    {
      label: "Últimos 7 dias",
      get: () => [
        moment().subtract(6, "days").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
      ],
    },
    {
      label: "Últimos 30 dias",
      get: () => [
        moment().subtract(29, "days").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
      ],
    },
    {
      label: "Este mês",
      get: () => [
        moment().startOf("month").format("YYYY-MM-DD"),
        moment().endOf("month").format("YYYY-MM-DD"),
      ],
    },
  ];
  const applyPreset = (fnGet) => {
    const [s, e] = fnGet();
    setStartDate(s);
    setEndDate(e);
  };
  const clearFilters = () => {
    setSelectedCustomer({ label: "Todos os clientes", value: 0 });
    setSelectedStatuses([]);
    setSelectedTypePayments([]);
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // ===================== QUERIES =====================
  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`
      );
      if (!data?.data?.data) throw new Error("Erro ao buscar clientes");
      let customers = data.data.data.map((customer) => ({
        label:
          customer.typeCustomer === "CPF"
            ? customer.name
            : customer.socialReason,
        value: customer.id,
        company: customer,
      }));
      customers = [{ label: "Todos os clientes", value: 0 }, ...customers];
      return customers;
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingInvoices,
    data: invoices,
    refetch: refetchInvoices,
  } = useQuery(
    [
      "invoices",
      startDate,
      endDate,
      selectedStatuses,
      selectedTypePayments,
      currentPage,
      selectedCustomer,
    ],
    async () => {
      setErrorInvoicesUI(false);
      const { data } = await axios.get(
        `${ADMIN.GET_PAYMENTS}?startDate=${startDate}&endDate=${endDate}&status=${selectedStatuses}&typePayment=${selectedTypePayments}&page=${currentPage}&companyId=${selectedCustomer?.value}`
      );
      setTotalPages(data.data.totalPages);
      setTotalCount(data.data.totalCount);
      setTotalPriceSum(data.data.totalPrice);
      return data.data.data;
    },
    { enabled: isActive, onError: () => setErrorInvoicesUI(true) }
  );

  // ===================== UTILS =====================
  const handleCheckboxChange = (value, setState) => {
    setState((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]
    );
  };
  const coercePaid = (isPaid) =>
    isPaid === true || isPaid === 1 || isPaid === "1";

  // ===================== AÇÕES (ATUALIZAR / EXCLUIR) =====================
  const handleUpdatePayment = async (type) => {
    setErrorPaymentConfirm(false);
    try {
      await axios.get(
        `${ADMIN.UPDATE_PAYMENT}/${selectedInvoice?.id}?companyId=${
          type === "CONFIRM_UPDATE" ? selectedInvoice?.companyId : ""
        }`
      );
      setOpenModalPaymentConfirm(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (e) {
      setErrorPaymentConfirm(true);
    }
  };

  const handleDeletePayment = async () => {
    setErrorPaymentDelete(false);
    try {
      await axios.delete(`${ADMIN.DELETE_PAYMENT}/${selectedInvoice?.id}`);
      setOpenModalPaymentDelete(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (e) {
      setErrorPaymentDelete(true);
    }
  };

  // ===================== PAGAMENTO MANUAL =====================
  const [openManualModal, setOpenManualModal] = useState(false);
  const [manualDueDate, setManualDueDate] = useState("");
  const [manualDescription, setManualDescription] = useState("");
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState("");
  const [selectedManualCompanies, setSelectedManualCompanies] = useState([]); // [{ companyId, name, value }]
  const [companySortAsc, setCompanySortAsc] = useState(true);

  const {
    control: modalControl,
    setValue,
    getValues,
    unregister,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {}, // cada empresa vira um campo dinâmico: company_<id>
  });

  // formatar exibição pt-BR
  const formatMoneyDisplay = (val) => {
    if (val === "" || val == null) return "";
    const n = Number(val);
    if (isNaN(n)) return "";
    return n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  // parse flexível (último separador é decimal)
  const parseMoneyFlexible = (input) => {
    if (input == null) return "";
    let s =
      typeof input === "string"
        ? input
        : typeof input === "number"
        ? String(input)
        : input?.target?.value ?? "";
    s = s.replace(/[^\d.,-]/g, "");
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    const sepIndex = Math.max(lastComma, lastDot);
    let intPart,
      fracPart = "";
    if (sepIndex >= 0) {
      intPart = s.slice(0, sepIndex).replace(/[^\d-]/g, "");
      fracPart = s.slice(sepIndex + 1).replace(/[^\d]/g, "");
    } else {
      intPart = s.replace(/[^\d-]/g, "");
    }
    if (fracPart.length > 2) fracPart = fracPart.slice(0, 2);
    if (intPart === "" && fracPart === "") return "";
    return fracPart ? `${intPart}.${fracPart}` : intPart;
  };

  const companyOptions = useMemo(() => {
    const arr = (listCustomers || []).filter((c) => c.value !== 0);
    if (!companyFilter) return arr;
    const f = companyFilter.toLowerCase();
    return arr.filter((c) => (c.label || "").toLowerCase().includes(f));
  }, [listCustomers, companyFilter]);

  const sortedCompanyOptions = useMemo(() => {
    const arr = [...(companyOptions || [])];
    arr.sort((a, b) => {
      const A = (a.label || "").toLowerCase();
      const B = (b.label || "").toLowerCase();
      return companySortAsc ? A.localeCompare(B) : B.localeCompare(A);
    });
    return arr;
  }, [companyOptions, companySortAsc]);

  const allSelectedIds = useMemo(
    () => new Set(selectedManualCompanies.map((s) => s.companyId)),
    [selectedManualCompanies]
  );

  const fieldName = (id) => `company_${id}`;

  const toggleSelectCompany = (c) => {
    setSelectedManualCompanies((prev) => {
      const exists = prev.find((x) => x.companyId === c.value);
      if (exists) {
        unregister(fieldName(c.value));
        return prev.filter((x) => x.companyId !== c.value);
      }
      setValue(fieldName(c.value), "");
      return [
        ...prev,
        {
          companyId: c.value,
          name: c.label,
          company: c.company, // <-- guarda o objeto completo
          value: "",
        },
      ];
    });
  };

  const removeCompany = (companyId) => {
    unregister(fieldName(companyId));
    setSelectedManualCompanies((prev) =>
      prev.filter((c) => c.companyId !== companyId)
    );
  };

  const updateCompanyValue = (companyId, val) => {
    const clean = parseMoneyFlexible(val);
    setSelectedManualCompanies((prev) =>
      prev.map((c) => (c.companyId === companyId ? { ...c, value: clean } : c))
    );
  };

  const companyFieldNames = useMemo(
    () => selectedManualCompanies.map((c) => fieldName(c.companyId)),
    [selectedManualCompanies]
  );

  // observar mudanças para recomputar o total
  useWatch({ control: modalControl, name: companyFieldNames });

  const getRowNumber = (id) => {
    const v = getValues(fieldName(id));
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const isRowInvalid = (id) => {
    const v = getValues(fieldName(id));
    const n = parseFloat(v);
    return !(v !== "" && !isNaN(n) && n > 0);
  };

  const totalManualValue = useMemo(() => {
    return selectedManualCompanies.reduce(
      (sum, c) => sum + getRowNumber(c.companyId),
      0
    );
  }, [watch(companyFieldNames.join("|")), selectedManualCompanies]);

  const canSubmitManual = useMemo(() => {
    if (!manualDueDate) return false;
    if (selectedManualCompanies.length === 0) return false;
    return selectedManualCompanies.every((c) => !isRowInvalid(c.companyId));
  }, [
    manualDueDate,
    watch(companyFieldNames.join("|")),
    selectedManualCompanies,
  ]);

  const submitManualPayment = async () => {
    setManualError("");
    setManualSuccess("");
    setManualLoading(true);
    try {
      const companiesPayload = selectedManualCompanies.map((c) => ({
        ...c.company, // <-- company completa
        adjustedTotalPrice: getRowNumber(c.companyId), // número já em 1234.56
      }));

      await axios.post(`${ADMIN.RUN_CLOSING}`, {
        dueDate: manualDueDate,
        description: manualDescription,
        companies: companiesPayload,
      });

      setManualSuccess("Pagamentos criados com sucesso.");
      setManualLoading(false);

      setTimeout(() => {
        setOpenManualModal(false);
        setSelectedManualCompanies([]);
        setManualDescription("");
        setManualDueDate("");
        companyFieldNames.forEach((n) => unregister(n));
        queryClient.invalidateQueries(["invoices"]);
      }, 900);
    } catch (err) {
      setManualLoading(false);
      setManualError(
        err?.response?.data?.message ||
          "Não foi possível criar os pagamentos. Tente novamente."
      );
    }
  };

  // ===================== ENHANCEMENTS VISUAIS DA TABELA =====================
  const [qText, setQText] = useState("");
  const [qStatus, setQStatus] = useState("ALL"); // ALL | PENDING | PAID
  const [qType, setQType] = useState("ALL"); // ALL | BOLETO | PIX
  const [dense, setDense] = useState(false);

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];
    return invoices.filter((inv) => {
      const paid = coercePaid(inv.isPaid);
      if (qStatus === "PENDING" && paid) return false;
      if (qStatus === "PAID" && !paid) return false;
      if (qType !== "ALL" && inv.type !== qType) return false;
      if (qText) {
        const name = inv?.Company?.name || inv?.Company?.socialReason || "";
        return name.toLowerCase().includes(qText.toLowerCase());
      }
      return true;
    });
  }, [invoices, qText, qStatus, qType]);

  const rowPadding = dense ? "py-1.5" : "py-2.5";
  const cellPadding = dense ? "py-1.5" : "py-2";

  const TypeBadge = ({ type }) => {
    const base = "px-2 py-0.5 rounded text-[11px] font-semibold";
    if (type === "PIX")
      return (
        <span className={`${base} bg-emerald-100 text-emerald-700`}>PIX</span>
      );
    if (type === "BOLETO")
      return (
        <span className={`${base} bg-indigo-100 text-indigo-700`}>BOLETO</span>
      );
    return (
      <span className={`${base} bg-gray-100 text-gray-600`}>{type || "-"}</span>
    );
  };

  const StatusBadge = ({ paid }) =>
    paid ? (
      <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-700">
        Pago
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700">
        Pendente
      </span>
    );

  // ===================== RENDER =====================
  return (
    <div>
      {/* Header Ações */}
      <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
        <div className="flex gap-2">
          <Button
            size="xs"
            onClick={() => setShowFilters((prev) => !prev)}
            className="primary text-white transition-all duration-300"
          >
            <div className="flex items-center gap-2 transition-all duration-300">
              <span>
                {showFilters ? "Esconder filtros" : "Mostrar filtros"}
              </span>
              <span className="transition-transform duration-300">
                {!showFilters ? <LuFilter /> : <LuFilterX />}
              </span>
            </div>
          </Button>
          <Button size="xs" color="light" onClick={clearFilters}>
            Limpar filtros
          </Button>
        </div>
        <div className="flex gap-2">
          <Button className="bg-gray-500" onClick={reactToPrintFn}>
            <div className="flex gap-2 items-center">
              <div>Imprimir</div>
              <MdLocalPrintshop fontSize={20} />
            </div>
          </Button>
          <Button color="success" onClick={() => setOpenManualModal(true)}>
            Gerar boleto manual
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-md shadow-sm border border-gray-200 p-4 relative">
            {renderFilters && (
              <div className="transition-opacity duration-500">
                {isLoadingCustomers && <DrawerLoading />}
                <div className="title-syncx">Filtro avançado</div>

                {/* Presets */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {presets.map((p) => (
                    <Button
                      key={p.label}
                      size="xs"
                      color="light"
                      onClick={() => applyPreset(p.get)}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-4">
                  <div className="mt-6 min-w-[250px]">
                    <AutoCompleteInput
                      options={listCustomers}
                      label="Cliente"
                      onChange={(customer) => setSelectedCustomer(customer)}
                      value={selectedCustomer?.value}
                      isLoading={isLoadingCustomers}
                    />
                    {errorListCustomers && (
                      <Alert color="failure" className="mt-2">
                        Ocorreu um erro ao buscar clientes
                      </Alert>
                    )}
                  </div>

                  <div>
                    <div className="font-semibold mt-3 text-sm mb-2">
                      Filtro por data de criação
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate)))
                            setEndDate(e.target.value);
                        }}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 focus:outline-none focus:ring-0"
                      />
                      <div className="text-xs">Até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-3 md:gap-20">
                    <div className="mt-3 text-sm">
                      <div className="font-semibold">Status</div>
                      <ul className="mt-3 flex flex-col md:flex-row gap-2 md:gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="pendente"
                            checked={selectedStatuses.includes(0)}
                            onChange={() =>
                              handleCheckboxChange(0, setSelectedStatuses)
                            }
                          />
                          <Label htmlFor="pendente" className="flex">
                            Pendente
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="pago"
                            checked={selectedStatuses.includes(1)}
                            onChange={() =>
                              handleCheckboxChange(1, setSelectedStatuses)
                            }
                          />
                          <Label htmlFor="pago" className="flex">
                            Pago
                          </Label>
                        </div>
                      </ul>
                    </div>

                    <div className="mt-3 text-sm">
                      <div className="font-semibold">Tipo de pagamento</div>
                      <ul className="mt-3 flex flex-col md:flex-row gap-2 md:gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="boleto"
                            checked={selectedTypePayments.includes("BOLETO")}
                            onChange={() =>
                              handleCheckboxChange(
                                "BOLETO",
                                setSelectedTypePayments
                              )
                            }
                          />
                          <Label htmlFor="boleto" className="flex">
                            Boleto
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="pix"
                            checked={selectedTypePayments.includes("PIX")}
                            onChange={() =>
                              handleCheckboxChange(
                                "PIX",
                                setSelectedTypePayments
                              )
                            }
                          />
                          <Label htmlFor="pix" className="flex">
                            Pix
                          </Label>
                        </div>
                      </ul>
                    </div>
                  </div>
                </div>

                {errorInvoicesUI && (
                  <Alert color="failure" className="mt-4">
                    Falha ao carregar os pagamentos.
                    <div className="mt-2">
                      <Button size="xs" color="light" onClick={refetchInvoices}>
                        Tentar novamente
                      </Button>
                    </div>
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resumo + Tabela + Paginação */}
      <div className="flex-1 print-padding print-content" ref={reportRef}>
        <div className="title-syncx mt-3 mb-4">Resultado da busca</div>

        {startDate && endDate && (
          <div className="italic text-sm mb-2 flex items-center gap-2">
            Informações baseadas no período de{" "}
            <Badge className="w-fit">
              {moment(startDate).format(DATE_FORMAT)}
            </Badge>{" "}
            até{" "}
            <Badge className="w-fit">
              {moment(endDate).format(DATE_FORMAT)}
            </Badge>
          </div>
        )}

        {/* Cards resumo */}
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-3 min-w-[500px]">
            <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#1E429F] to-[#3B82F6] relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Valor Total</p>
                  <h2 className="text-2xl font-bold mt-1">
                    {!isLoadingInvoices ? (
                      formatToReal(totalPriceSum)
                    ) : (
                      <div className="animate-pulse h-6 bg-white/30 rounded w-28" />
                    )}
                  </h2>
                </div>
                <MdOutlineAttachMoney className="text-white text-4xl opacity-20" />
              </div>
            </div>
            <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Total de Pagamentos</p>
                  <h2 className="text-2xl font-bold mt-1">
                    {!isLoadingInvoices ? (
                      totalCount
                    ) : (
                      <div className="animate-pulse h-6 bg-white/30 rounded w-24" />
                    )}
                  </h2>
                </div>
                <BsFillBarChartFill className="text-white text-4xl opacity-20" />
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar da tabela */}
        <div className="mt-2 mb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex items-center gap-2">
            <TextInput
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Buscar por cliente…"
            />
            <select
              value={qStatus}
              onChange={(e) => setQStatus(e.target.value)}
              className="text-sm border rounded-md px-2 py-2"
            >
              <option value="ALL">Todos status</option>
              <option value="PENDING">Pendente</option>
              <option value="PAID">Pago</option>
            </select>
            <select
              value={qType}
              onChange={(e) => setQType(e.target.value)}
              className="text-sm border rounded-md px-2 py-2"
            >
              <option value="ALL">Todos tipos</option>
              <option value="BOLETO">Boleto</option>
              <option value="PIX">Pix</option>
            </select>
            <Button
              color="light"
              size="xs"
              onClick={() => setDense((d) => !d)}
              title="Alternar densidade"
            >
              {dense ? "Denso" : "Padrão"}
            </Button>
          </div>
          <div className="hidden md:flex">
            <Button className="bg-gray-500" onClick={reactToPrintFn}>
              <div className="flex gap-2 items-center">
                <div>Imprimir</div>
                <MdLocalPrintshop fontSize={20} />
              </div>
            </Button>
          </div>
        </div>

        {/* Botão imprimir mobile */}
        <div className="mb-4 mt-2 flex md:hidden justify-end">
          <Button className="bg-gray-500" onClick={reactToPrintFn}>
            <div className="flex gap-2 items-center">
              <div>Imprimir</div>
              <MdLocalPrintshop fontSize={20} />
            </div>
          </Button>
        </div>

        <small
          className="gap-2 items-center mt-1 mb-2 flex md:hidden justify-center"
          style={{ fontSize: 10 }}
        >
          <span>Deslize para o lado para visualizar todas as informações</span>{" "}
          <FaArrowsLeftRight />
        </small>

        {/* Tabela principal */}
        {filteredInvoices?.length > 0 || isLoadingInvoices ? (
          <div className="overflow-x-auto shadow-sm rounded-xl mt-0 md:mt-3 border border-gray-200">
            <Table striped style={{ fontSize: "13px" }}>
              <Table.Head>
                <Table.HeadCell className="bg-gray-50/70">
                  Cliente
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Criado em
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Vencimento
                </Table.HeadCell>
                <Table.HeadCell className="text-right bg-gray-50/70">
                  Valor
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Tipo
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Status
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Ações
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {!isLoadingInvoices ? (
                  <>
                    {filteredInvoices.map((invoice, idx) => {
                      const paid = coercePaid(invoice.isPaid);
                      return (
                        <Table.Row
                          key={idx}
                          className={`bg-white hover:bg-gray-50 transition-colors ${rowPadding}`}
                        >
                          <Table.Cell
                            className={`font-medium text-gray-900 ${cellPadding}`}
                          >
                            <div className="leading-tight">
                              <div className="font-semibold">
                                {invoice?.Company?.name ||
                                  invoice?.Company?.socialReason}
                              </div>
                              <small className="block text-xs text-gray-500">
                                {invoice?.Company?.status == 0 ? (
                                  <span className="text-red-500">
                                    Bloqueado
                                  </span>
                                ) : invoice?.Company?.status === -1 ? (
                                  <span className="text-red-500">
                                    Inadimplente
                                  </span>
                                ) : (
                                  <span className="text-green-600">Ativo</span>
                                )}
                              </small>
                            </div>
                          </Table.Cell>
                          <Table.Cell
                            className={`text-center text-gray-900 ${cellPadding}`}
                          >
                            {moment(invoice.createdAt).format(DATE_FORMAT)}
                          </Table.Cell>
                          <Table.Cell
                            className={`text-center text-gray-900 ${cellPadding}`}
                          >
                            {invoice.dueDate
                              ? moment(invoice.dueDate).format(DATE_FORMAT)
                              : "-"}
                          </Table.Cell>
                          <Table.Cell
                            className={`text-right tabular-nums text-gray-900 font-semibold ${cellPadding}`}
                          >
                            {formatToReal(invoice?.price)}
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellPadding}`}>
                            <TypeBadge type={invoice.type} />
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellPadding}`}>
                            <StatusBadge paid={paid} />
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellPadding}`}>
                            <div className="flex justify-center gap-2">
                              {invoice.paymentLink && (
                                <a
                                  className="inline-block text-center"
                                  href={invoice.paymentLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Link de pagamento"
                                >
                                  <FiExternalLink fontSize={18} />
                                </a>
                              )}
                              {invoice.type === "BOLETO" && !paid && (
                                <button
                                  className="inline-block text-center cursor-pointer"
                                  title="Sinalizar como pago"
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setOpenModalPaymentConfirm(true);
                                  }}
                                >
                                  <IoMdCheckmarkCircleOutline
                                    className="text-green-500"
                                    fontSize={18}
                                  />
                                </button>
                              )}
                              {!paid && (
                                <button
                                  className="inline-block text-center cursor-pointer"
                                  title="Excluir cobrança"
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setOpenModalPaymentDelete(true);
                                  }}
                                >
                                  <MdDeleteOutline
                                    className="text-red-500"
                                    fontSize={18}
                                  />
                                </button>
                              )}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {Array.from({ length: 10 }).map((_, index) => (
                      <Table.Row
                        key={index}
                        className={`bg-white ${rowPadding}`}
                      >
                        {Array.from({ length: 7 }).map((__, i) => (
                          <Table.Cell key={i} className="text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </>
                )}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <EmptyTable icon="search_off">
            Nenhum registro encontrado com esses filtros
          </EmptyTable>
        )}

        {totalPages > 1 && (
          <div className="flex overflow-x-auto justify-center mt-3">
            <Pagination
              layout="pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              previousLabel=""
              nextLabel=""
              showIcons
            />
          </div>
        )}
      </div>

      {/* Modal Confirmar Pagamento */}
      <Modal
        show={openModalPaymentConfirm}
        size="lg"
        onClose={() => setOpenModalPaymentConfirm(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <h3 className="mb-2 text-lg font-normal text-gray-600">
              Deseja sinalizar este boleto como pago?
            </h3>
            <p className="text-sm text-gray-500">
              Para dar baixa e desbloquear o cliente, clique no botão{" "}
              <b>"Confirmar e Desbloquear"</b>
            </p>
            <div className="flex flex-col md:flex-row justify-center gap-2 mt-5">
              <Button
                color="success"
                onClick={() => handleUpdatePayment("CONFIRM")}
              >
                Apenas confirmar
              </Button>
              <Button
                color="gray"
                onClick={() => handleUpdatePayment("CONFIRM_UPDATE")}
              >
                Confirmar e desbloquear
              </Button>
            </div>
            {errorPaymentConfirm && (
              <Alert className="mt-4" color="failure" withBorderAccent>
                Ops! Ocorreu algum erro, tente novamente mais tarde!
              </Alert>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Modal Excluir Pagamento */}
      <Modal
        show={openModalPaymentDelete}
        size="md"
        onClose={() => setOpenModalPaymentDelete(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
            <h3 className="mb-5 text-lg font-normal text-gray-600">
              Tem certeza que deseja excluir essa cobrança?
            </h3>
            <div className="flex justify-center gap-2">
              <Button color="failure" onClick={handleDeletePayment}>
                Excluir
              </Button>
              <Button
                color="gray"
                onClick={() => setOpenModalPaymentDelete(false)}
              >
                Cancelar
              </Button>
            </div>
            {errorPaymentDelete && (
              <Alert className="mt-4" color="failure" withBorderAccent>
                Ops! Ocorreu algum erro, tente novamente mais tarde!
              </Alert>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Modal Pagamento Manual */}
      <Modal
        show={openManualModal}
        size="5xl"
        onClose={() => {
          setOpenManualModal(false);
          setManualError("");
          setManualSuccess("");
        }}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold">
                  Criar pagamento manual
                </h3>
                <p className="text-sm text-gray-500">
                  Gere boletos manuais selecionando empresas e definindo o valor
                  individual.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge color="info">
                  Selecionadas: {selectedManualCompanies.length}
                </Badge>
                <Badge color="purple">
                  Total: {formatToReal(totalManualValue)}
                </Badge>
              </div>
            </div>

            {manualError && (
              <Alert color="failure" withBorderAccent>
                {manualError}
              </Alert>
            )}
            {manualSuccess && (
              <Alert color="success" withBorderAccent>
                {manualSuccess}
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="col-span-1">
                <Label>Data de vencimento</Label>
                <input
                  type="date"
                  value={manualDueDate}
                  onChange={(e) => setManualDueDate(e.target.value)}
                  className="w-full block py-2 px-2 rounded-md text-sm text-gray-700 bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Descrição (opcional)</Label>
                <Textarea
                  rows={2}
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder="Ex.: Cobrança complementar referente a ..."
                />
              </div>
            </div>

            {/* Empresas */}
            <div className="grid grid-cols-1 gap-4">
              <div className="shadow-md rounded-md p-3 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Empresas</div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => setCompanySortAsc((v) => !v)}
                    >
                      {companySortAsc ? "A–Z" : "Z–A"}
                    </Button>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Buscar</Label>
                    <input
                      placeholder="Filtrar por nome"
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      className="w-full block py-2 px-3 rounded-md text-sm text-gray-700 bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button
                      color="light"
                      size="xs"
                      onClick={() => {
                        const toAdd = sortedCompanyOptions.filter(
                          (c) => !allSelectedIds.has(c.value)
                        );
                        setSelectedManualCompanies((prev) => [
                          ...prev,
                          ...toAdd.map((c) => ({
                            companyId: c.value,
                            name: c.label,
                            company: c.company, // <-- objeto completo
                            value: "",
                          })),
                        ]);
                      }}
                    >
                      Selecionar todas
                    </Button>
                  </div>
                </div>

                <div className="mt-3 border rounded-lg max-h-64 overflow-auto">
                  <div className="divide-y">
                    {(sortedCompanyOptions || []).map((c) => {
                      const checked = allSelectedIds.has(c.value);
                      return (
                        <label
                          key={c.value}
                          className={`flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer ${
                            checked ? "bg-indigo-50" : "bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelectCompany(c)}
                              className="accent-indigo-600"
                            />
                            <span className="truncate max-w-[260px]">
                              {c.label}
                            </span>
                          </div>
                          {checked && <Badge color="indigo">Selecionada</Badge>}
                        </label>
                      );
                    })}
                    {sortedCompanyOptions?.length === 0 && (
                      <div className="p-4 text-xs text-gray-500">
                        Nenhuma empresa encontrada
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Selecionadas & valores (tabela alinhada) */}
              <div className="shadow-md rounded-md p-3 border border-gray-200">
                <div className="border rounded-lg overflow-hidden">
                  <Table style={{ fontSize: 13 }} className="w-full">
                    <Table.Head className="w-full">
                      <Table.HeadCell className="bg-gray-100">
                        Empresa
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100">
                        Valor (R$)
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        <div className="">Ação</div>
                      </Table.HeadCell>
                    </Table.Head>
                  </Table>
                  <div className="max-h-56 overflow-auto">
                    <Table style={{ fontSize: 13 }}>
                      <Table.Body className="divide-y">
                        {selectedManualCompanies.length === 0 && (
                          <Table.Row>
                            <Table.Cell
                              colSpan={3}
                              className="text-center text-xs text-gray-500 py-6"
                            >
                              Nenhuma empresa selecionada
                            </Table.Cell>
                          </Table.Row>
                        )}

                        {selectedManualCompanies.map((c) => {
                          const invalid = isRowInvalid(c.companyId);

                          return (
                            <Table.Row key={c.companyId}>
                              <Table.Cell className="align-middle py-2">
                                <div className="truncate max-w-[280px]">
                                  {c.name}
                                </div>
                              </Table.Cell>

                              <Table.Cell className="text-left py-2">
                                <div className="inline-flex w-full">
                                  <CurrencyInput
                                    control={modalControl}
                                    name={fieldName(c.companyId)}
                                    required
                                    inputMode="decimal"
                                    placeholder=""
                                    className="w-44"
                                  />
                                </div>
                                {invalid && (
                                  <small className="text-red-500 block text-right">
                                    Informe um valor &gt; 0
                                  </small>
                                )}
                              </Table.Cell>

                              <Table.Cell className="text-center py-1">
                                <div className="flex items-center justify-center">
                                  <Button
                                    color="failure"
                                    size="xs"
                                    onClick={() => removeCompany(c.companyId)}
                                  >
                                    <BsTrash />
                                  </Button>
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>

                  {/* Totalizador */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                    <div className="text-xs text-gray-500">
                      {selectedManualCompanies.length} empresa(s) selecionada(s)
                    </div>
                    <div className="text-sm flex items-center">
                      <span className="mr-2 text-gray-500">Total</span>
                      <Badge color="purple" className="text-sm">
                        {formatToReal(totalManualValue)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Rodapé */}
            <div className="sticky bottom-0 bg-white border-t pt-3 mt-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-xs text-gray-500">
                  {(!manualDueDate || selectedManualCompanies.length === 0) && (
                    <span>
                      Defina o vencimento e selecione ao menos 1 empresa.
                    </span>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    color="gray"
                    onClick={() => setOpenManualModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    color="success"
                    disabled={!canSubmitManual || manualLoading}
                    onClick={submitManualPayment}
                  >
                    {manualLoading ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <span>Processando…</span>
                      </div>
                    ) : (
                      "Criar pagamentos"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default AdminPaymentsTable;
